import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import { useGetFreightMarginTargetsQuery, useSetFreightMarginTargetMutation } from '../../redux/freightPnlApiSlice'
import { count } from './freightPnlUtils'

// The backend's earlier values (V2_LIVE / V2_SHADOW / V1) map too, so the tab reads right across a staggered deploy.
const PACE_LABEL = {
  LIVE: 'PACE live', SHADOW: 'PACE shadow', OFF: 'Rule order only',
  V2_LIVE: 'PACE live', V2_SHADOW: 'PACE shadow', V1: 'Rule order only',
}

const pctText = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`)

/** "−3.2 pts" against a reference; empty when either side is missing. */
function gapText(cur, ref) {
  if (cur === null || cur === undefined || ref === null || ref === undefined) return ''
  const d = Number(cur) - Number(ref)
  return `${d < 0 ? '−' : '+'}${Math.abs(d).toFixed(1)} pts`
}

/** One window: margin, AWBs, and the gap to each reference, red when that reference is breached. */
function WindowCell({ w, target, lastYear, belowTarget, belowLastYear, minShipments }) {
  const thin = w.marginPct !== null && w.marginPct !== undefined && w.pricedShipments < minShipments
  return (
    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
      <Box sx={{ fontWeight: 600 }}>{pctText(w.marginPct)}</Box>
      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
        {count(w.pricedShipments)} AWBs{thin ? ' · too few to flag' : ''}
      </Typography>
      {target !== null && target !== undefined && w.marginPct !== null && w.marginPct !== undefined && (
        <Typography variant='caption' sx={{ display: 'block', color: belowTarget ? 'error.main' : 'text.secondary', fontWeight: belowTarget ? 600 : 400 }}>
          {gapText(w.marginPct, target)} vs target
        </Typography>
      )}
      {lastYear?.marginPct !== null && lastYear?.marginPct !== undefined && w.marginPct !== null && w.marginPct !== undefined && (
        <Typography variant='caption' sx={{ display: 'block', color: belowLastYear ? 'error.main' : 'text.secondary', fontWeight: belowLastYear ? 600 : 400 }}>
          {gapText(w.marginPct, lastYear.marginPct)} vs last year
        </Typography>
      )}
    </TableCell>
  )
}

/**
 * Margin targets (PACE+ margin report): every merchant with a target or on PACE (live or shadow), margin over the last 7 days and
 * month to date against its target and against the same windows last year. Ignores the page's filters: the windows
 * end on the "as of" day. Setting a target never changes allocation; the 09:05 mail flags breaches.
 */
export default function MarginTargetsTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [asOf, setAsOf] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'))
  const { data, isFetching, error } = useGetFreightMarginTargetsQuery(asOf)
  const [setTarget, { isLoading: saving }] = useSetFreightMarginTargetMutation()

  const { data: merchantsRaw = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchants = merchantsRaw
    .filter(m => m.is_deleted === '0')
    .map(m => ({ label: m.companyName ? `${m.name} (${m.companyName})` : m.name, value: m.id }))
  const [merchant, setMerchant] = useState(null)
  const [pct, setPct] = useState('')

  const save = async (merchantOid, value) => {
    try {
      await setTarget({ merchantOid, pct: value }).unwrap()
      enqueueSnackbar(value === null || value === '' ? 'Target cleared' : `Target set to ${Number(value).toFixed(1)}%`, { variant: 'success' })
      setMerchant(null)
      setPct('')
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not save the target', { variant: 'error' })
    }
  }

  const pctValid = pct !== '' && Number(pct) >= 0 && Number(pct) < 100
  const rows = data?.rows || []
  const minShipments = data?.minShipments ?? 50

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <TextField
          size='small' type='date' label='As of' value={asOf} InputLabelProps={{ shrink: true }}
          inputProps={{ max: format(subDays(new Date(), 1), 'yyyy-MM-dd') }}
          onChange={(e) => e.target.value && setAsOf(e.target.value)}
        />
        <Box sx={{ flex: 1 }} />
        <Autocomplete
          size='small' options={merchants} value={merchant} sx={{ minWidth: 260 }}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(_, v) => {
            setMerchant(v)
            const existing = rows.find(r => r.merchantOid === v?.value)
            setPct(existing?.targetPct ?? '')
          }}
          renderInput={(params) => <TextField {...params} placeholder='Merchant' />}
        />
        <TextField
          size='small' label='Target margin %' type='number' value={pct} sx={{ width: 150 }}
          inputProps={{ min: 0, max: 99.9, step: 0.5 }} onChange={(e) => setPct(e.target.value)}
          error={pct !== '' && !pctValid} helperText={pct !== '' && !pctValid ? '0 to below 100' : undefined}
        />
        <Button
          variant='contained' size='small' disabled={!merchant || !pctValid || saving}
          onClick={() => save(merchant.value, pct)} sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Set target
        </Button>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity='error' sx={{ mb: 1 }}>{error?.data?.message || error?.message || 'Could not load margin targets'}</Alert>}
      {data?.missingDays?.length > 0 && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          Not computed yet: {data.missingDays.join(', ')}. These days are left out of the figures.
        </Alert>
      )}

      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Merchant</TableCell>
              <TableCell align='right'>Target</TableCell>
              <TableCell align='right'>Last 7 days</TableCell>
              <TableCell align='right'>Month to date</TableCell>
              <TableCell align='right'>Last 7 days, last year</TableCell>
              <TableCell align='right'>Month to date, last year</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.merchantOid} sx={r.breached ? { bgcolor: 'rgba(179, 38, 30, 0.04)' } : undefined}>
                <TableCell>
                  <Box sx={{ fontWeight: 600 }}>{r.merchantName}</Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip size='small' variant='outlined' label={PACE_LABEL[r.paceMode] || r.paceMode} />
                    {r.breached && <Chip size='small' color='error' label='Flagged' />}
                  </Box>
                </TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                  {pctText(r.targetPct)}
                  <Tooltip title='Change target'>
                    <IconButton size='small' onClick={() => { setMerchant(merchants.find(m => m.value === r.merchantOid) || { label: r.merchantName, value: r.merchantOid }); setPct(r.targetPct ?? '') }}>
                      <EditOutlinedIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                  {r.targetPct !== null && r.targetPct !== undefined && (
                    <Tooltip title='Clear target'>
                      <IconButton size='small' disabled={saving} onClick={() => save(r.merchantOid, null)}>
                        <ClearOutlinedIcon fontSize='inherit' />
                      </IconButton>
                    </Tooltip>
                  )}
                  {r.targetFarFromLastYear && (
                    <Typography variant='caption' sx={{ display: 'block', color: 'warning.main' }}>
                      {data.farFromYoyPts}+ pts from last year
                    </Typography>
                  )}
                </TableCell>
                <WindowCell w={r.last7} target={r.targetPct} lastYear={r.last7LastYear} belowTarget={r.last7BelowTarget} belowLastYear={r.last7BelowLastYear} minShipments={minShipments} />
                <WindowCell w={r.mtd} target={r.targetPct} lastYear={r.mtdLastYear} belowTarget={r.mtdBelowTarget} belowLastYear={r.mtdBelowLastYear} minShipments={minShipments} />
                <TableCell align='right'>
                  {pctText(r.last7LastYear?.marginPct)}
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                    {r.last7LastYear?.marginPct === null || r.last7LastYear?.marginPct === undefined ? 'no prior-year data' : `${count(r.last7LastYear.pricedShipments)} AWBs`}
                  </Typography>
                </TableCell>
                <TableCell align='right'>
                  {pctText(r.mtdLastYear?.marginPct)}
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                    {r.mtdLastYear?.marginPct === null || r.mtdLastYear?.marginPct === undefined ? 'no prior-year data' : `${count(r.mtdLastYear.pricedShipments)} AWBs`}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!isFetching && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No merchant has a target or is on PACE yet. Set a target above.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        Lists every merchant with a margin target, plus PACE merchants (live or shadow) for the year-on-year check. Margin is
        (sell − courier buy) ÷ sell on priced in-scope D2C AWBs, excluding GST, the Loss Making Clients mail's margin, before weight
        discrepancy, lost and damaged claims. A window is flagged when it is below the target or below the same calendar window last
        year; windows with fewer than {minShipments} priced AWBs are never flagged. The page's date and merchant filters do not apply here.
        The daily 09:05 mail lists flagged merchants only. Targets never change allocation settings.
      </Typography>
    </Box>
  )
}
